<template>
    <div
        class="relative px-2 pt-2 pb-6 text-xs"
        :class="annotation.original_has_changed ? 'bg-gray-100' : 'bg-white cursor-pointer'"
        @mouseover="hoverOn"
        @mouseleave="hoverOff"
    >
        <div v-if="annotation.original_has_changed" class="font-bold italic mb-2 text-red-700 text-xxs">
            {{ $t('Archived') }}
        </div>

        <article :aria-labelledby="'entry-heading-' + annotation.id">
            <h3 :id="'entry-heading-' + annotation.id"
                class="m-0 p-0"
            >
                <span class="block pb-2 font-medium text-xxs uppercase" :class="getTypeTextClass(annotation.type)">
                    {{ annotation.type }}
                </span>

                <span class="sr-only">:</span>

                <span class="block rounded text-xs p-2 font-semibold leading-snug text-gray-800"
                    :class="[ getTypeBackgroundClass(annotation.type), getTypeBorderClass(annotation.type)]"
                >
                    {{ annotation.selected_text }}
                </span>
            </h3>

            <a :href="'#' + annotation.render_id"
                :aria-label="$t('View highlight in context')"
                class="absolute top-0 right-0 rounded-full w-6 h-6 p-0 flex items-center justify-center no-underline m-1"
            >
                <i aria-hidden="true" class="text-lg text-gray-750 hover:text-indigo-500 material-icons previeweye">remove_red_eye</i>
            </a>
        </article>


        <dl v-if="annotation.comments && annotation.comments.length" class="mt-1">
            <div v-for="comment in annotation.comments" class="leading-snug mt-2"
                :class="annotation.original_has_changed ? 'text-gray-800' : 'text-black'"
            >
                <dt class="font-semibold inline">{{ self_id == comment.author_id ? 'You' : comment.author_name }}:</dt>

                <dd class="italic inline">{{ comment.comment }}</dd>
            </div>

            <template v-if="! read_only">
                <div v-show="! adding_comment && ! showing_menu" class="absolute -mb-3 left-0 bottom-0 w-full flex">
                    <button class="bg-indigo-600 cursor-pointer hover:bg-indigo-700 mx-auto px-4 py-1 rounded-sm text-center text-white text-xxs uppercase"
                        @click="addComment(annotation)"
                    >
                        {{ $t('Add Comment') }}
                    </button>
                </div>
            </template>
        </dl>

        <div v-else>
            <div v-if="self_id != annotation.author_id"
                class="leading-snug mt-2"
            >
                {{ $t('Annotation by') }} <span class="text-black font-semibold">{{ annotation.author_name}}</span>
            </div>

            <template v-if="! read_only">
                <div v-show="! adding_comment && ! showing_menu" class="absolute -mb-3 left-0 bottom-0 w-full flex">
                    <button class="bg-indigo-600 cursor-pointer hover:bg-indigo-700 mx-auto px-4 py-1 rounded-sm text-center text-white text-xxs uppercase"
                        @click="addComment(annotation)"
                    >
                        {{ $t('Add Comment') }}
                    </button>
                </div>
            </template>
        </div>

        <template v-if="! read_only">
            <annotation-list-item-add-comment v-if="adding_comment"
                :annotation="annotation"
                :self_id="self_id"
                @clear="closeAddComment"
                ref="addComment"
                @updateComments="$emit('updateComments', $event)"
            />

            <div v-else class="flex justify-end">
                <button v-if="! showing_menu"
                    class="flex bg-transparent hover:bg-transparent text-gray-750 text-red-500 hover:text-indigo-500 rounded-full items-center justify-center w-6 h-6 p-0 mt-1 -mb-5 -mr-1"
                    @click="showMenu"
                    :aria-label="$t('Show annotation settings')"
                    ref="showSettings"
                >
                    <i class="text-base material-icons">settings</i>
                </button>

                <annotation-list-item-menu v-else
                    class="w-full"
                    :annotation="annotation"
                    @clear="hideMenu"
                    @deleteAnnotation="$emit('deleteAnnotation', $event)"
                />
            </div>
        </template>
    </div>
</template>

<script>
import AnnotationListItemAddComment from './annotation-list-item-add-comment.vue';
import AnnotationListItemMenu from './annotation-list-item-menu.vue';

const TYPES = {
    highlight: {
        text: 'text-yellow-800',
        border: 'border border-solid border-yellow-700',
        background: 'bg-yellow-300',
    },
    strikethrough: {
        text: 'text-red-600',
        border: '',
        background: 'annotation-show annotation-strikethrough',
    },
    task: {
        text: 'text-green-700',
        border: 'border-2 border-dotted border-green-600',
        background: 'bg-green-200',
    },
    comment: {
        text: 'text-purple-700',
        border: 'border border-dashed border-purple-500',
        background: 'bg-purple-200',
    },
    private: {
        text: 'text-orange-700',
        border: 'border-2 border-double border-orange-600',
        background: 'bg-orange-200',
    },
    journal: {
        text: 'text-teal-700',
        border: 'border-2 border-double border-teal-600',
        background: 'bg-teal-200',
    },
};

export default {
    components: {
        AnnotationListItemAddComment,
        AnnotationListItemMenu,
    },

    props: {
        annotation: null,
        self_id: null,
        hover: { default: true },
        read_only: { default: false },
    },

    data() {
        return {
            adding_comment: false,
            showing_menu: false,
        }
    },

    methods: {
        getTypeBackgroundClass(type) {
            return TYPES[type].background;
        },
        getTypeBorderClass(type) {
            return TYPES[type].border;
        },
        getTypeTextClass(type) {
            return TYPES[type].text;
        },
        getRenderedElement(render_id) {
            return document.querySelector("[data-selection-id='" + render_id + "']");
        },
        hoverOn() {
            if (this.hover && ! this.read_only && ! this.annotation.original_has_changed) {
                let el = this.getRenderedElement(this.annotation.render_id);
                el.classList.add('annotation-hover');
            }
        },
        hoverOff() {
            if (this.hover && ! this.read_only && ! this.annotation.original_has_changed) {
                let el = this.getRenderedElement(this.annotation.render_id);
                el.classList.remove('annotation-hover');
            }
        },
        addComment() {
            this.adding_comment = true;
        },
        closeAddComment() {
            this.adding_comment = false;
            this.$nextTick(() => {
                this.$refs.addComment?.focus();
            });
        },
        showMenu() {
            this.showing_menu = true;
        },
        hideMenu() {
            this.showing_menu = false;
            this.$nextTick(() => {
                this.$refs.showSettings?.focus();
            });
        },
    },
}
</script>
